import React, { useEffect, useState, Fragment, useRef } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Navbar,
  Row
} from "react-bootstrap";
import _ from "lodash";
import moment from "moment";
import { HorizontalLinearStepper } from "@cdk-uip/react-stepper";
import { toast } from "react-toastify";

import CSRForm from "./CSRForm";
import ConfidentialityAgreement from "./ConfidentialityAgreement";
import ContactDetailsForm from "./contactDetailsForm/ContactDetailsForm";
import EPFForm11 from "./EPFForm11";
import EPFForm2 from "./EPFForm2";
import EPFPartB from "./EPFPartB";
import GratuityFormF from "./GratuityFormF";
import IdentityCardForm from "./IdentityCardForm";
import PersonalDetailsForm from "./PersonalDetailsForm";

import { onBoardingActions } from "../../../actions/onBoardingActions";
import { candidateActions } from "../../../actions/candidateActions";
import api from "../../../api/onBoarding.resource";
import {
  CANDIDATE_STATUS,
  defaultToastOptions,
  onBoardingSteps,
  ROLES
} from "../../../constants/constants";
import BankingInformation from "./contactDetailsForm/bankingInfo/BankingInformation";
import SubmitPage from "./OnboardingSubmitPage";
import { getAge, encrypt, scrollToTopLeft } from "./../../../util/helpers";
import {
  getBankIFSCCode,
  getBankLocation,
  getBankName
} from "./../../../util/onBoardingDataFilters";

import "react-toastify/dist/ReactToastify.css";

const OnboardingForms = ({
  addEPFFrom2Details,
  addGratuityDetails,
  addRelationDetails,
  addressDetails,
  deleteEPFFrom2Details,
  deleteGratuityDetails,
  deleteRelationDetails,
  genders,
  generalDetails,
  getGendersList,
  getMaritalStatusList,
  getPersonalDetails,
  getRelationShipList,
  getStatesList,
  history,
  location,
  maritalStatusList,
  otherBankDetails,
  personalDetails,
  relationShipList,
  states,
  updateNomineeDetails,
  updateOtherBankDetails,
  updatePersonalDetails
}) => {
  const { state: { candidateId = "" } = {} } = location;
  const [allowFormSubmission, setAllowFormSubmission] = useState();
  const [hasUserSubmitted, setHasUserSubmitted] = useState();
  const user = JSON.parse(window.sessionStorage.getItem("user"));

  // When user other than candidate tries to navigate directly to any other pages other than candidate list.
  if (!candidateId && user.roleId !== ROLES.CANDIDATE) {
    history.push("/CandidateList");
  }

  useEffect(() => {
    getPersonalDetails(candidateId || user.id || "");
  }, []);

  useEffect(() => {
    if (allowFormSubmission) {
      getGendersList();
      getMaritalStatusList();
      getRelationShipList();
      getStatesList();
    }
  }, [allowFormSubmission]);

  const [activeStep, setActiveStep] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const PersonalDetailRef = useRef(null);
  const ContactDetailRef = useRef(null);
  const GratuityFormRef = useRef(null);
  const EPFForm11Ref = useRef(null);
  const EPFForm2Ref = useRef(null);
  const SEZFormRef = useRef(null);
  const CSRFormRef = useRef(null);
  const BankFormRef = useRef(null);

  const steps =
    personalDetails.cDKEmployeeType === 2 ||
    personalDetails.cDKEmployeeType === 3
      ? [
          "PersonalDetailsForm",
          "ContactDetailsForm",
          "IdentityCardForm",
          "BankingInformation",
          "ConfidentialityAgreement"
        ]
      : Object.keys(onBoardingSteps);

  const isValidForm = step => {
    switch (step) {
      case "PersonalDetailsForm":
        return (
          PersonalDetailRef.current && PersonalDetailRef.current.checkValidity()
        );
      case "ContactDetailsForm":
        return (
          ContactDetailRef.current && ContactDetailRef.current.checkValidity()
        );
      case "GratuityFormF":
        return (
          GratuityFormRef.current &&
          GratuityFormRef.current.checkValidity() &&
          !!isGratuityNomineeAdded()
        );
      case "EPFForm2":
        return (
          EPFForm2Ref.current &&
          EPFForm2Ref.current.checkValidity() &&
          !!isEPFNomineeAdded()
        );
      case "EPFPartB":
        return isWidowPensionNomineeSelected();
      case "EPFForm11":
        return EPFForm11Ref.current && EPFForm11Ref.current.checkValidity();
      case "IdentityCardForm":
        return SEZFormRef.current && SEZFormRef.current.checkValidity();
      case "CSRForm":
        return CSRFormRef.current && CSRFormRef.current.checkValidity();
      case "BankingInformation":
        return BankFormRef.current && BankFormRef.current.checkValidity();
      default:
        return true;
    }
  };

  useEffect(() => {
    setIsValid(isValidForm(steps[activeStep]));
  });

  if (
    user.roleId === ROLES.CANDIDATE &&
    generalDetails.status === CANDIDATE_STATUS.JOINED.id
  ) {
    return <SubmitPage showSubmitSuccessMessage={hasUserSubmitted} />;
  } else if (generalDetails.status && !allowFormSubmission) {
    // When candidate user tries to navigate directly to Onboarding Forms before onboarding phase.
    if (
      user.roleId === ROLES.CANDIDATE &&
      generalDetails.status < CANDIDATE_STATUS.ONBOARDING.id
    ) {
      history.push("/CandidateForm", { candidateId });
    }
    setAllowFormSubmission(true);
  }

  const filterTempIDs = inputDataArray =>
    inputDataArray.map(inputData =>
      _.omitBy(
        inputData,
        (value, key) => key === "id" && value.toString().startsWith("temp_")
      )
    );

  const saveOnBoardingDetails = async (
    evt,
    isSubmit = false,
    disableToast = false
  ) => {
    let updatedFamilyMembers = filterTempIDs(personalDetails.familyMembers);
    const isLoggedInUserAdminOrHROPs = [ROLES.ADMIN, ROLES.HROPS].includes(
      user.roleId
    );
    updatedFamilyMembers = updatedFamilyMembers.map(member => {
      return { ...member, age: getAge(member.dateOfBirth) };
    });
    const onBoardingDetails = {
      ...generalDetails,
      updatedBy: user.id,
      userName: personalDetails.emailId, //Temporarily updating User Name with Email Id
      status:
        (isLoggedInUserAdminOrHROPs &&
          generalDetails.status === CANDIDATE_STATUS.JOINED.id) ||
        isSubmit
          ? CANDIDATE_STATUS.JOINED.id
          : CANDIDATE_STATUS.ONBOARDING.id,
      userDetails: {
        ...personalDetails,
        bankBranch: encrypt(personalDetails.bankBranch),
        bankName: encrypt(
          getBankName(personalDetails.bankName, otherBankDetails.otherBankName)
        ),
        bankIFSCCode: encrypt(
          getBankIFSCCode(
            personalDetails.bankIFSCCode,
            otherBankDetails.otherIFSCCode
          )
        ),
        bankLocation: encrypt(
          getBankLocation(
            personalDetails.bankLocation,
            otherBankDetails.otherBankLocation
          )
        ),
        bankAccountNumber: encrypt(personalDetails.bankAccountNumber),
        otherIFSCCode: encrypt(personalDetails.otherIFSCCode),
        pfUanNo: encrypt(personalDetails.pfUanNo),
        panCardNo: encrypt(personalDetails.panCardNo),
        adhaarNumber: encrypt(personalDetails.adhaarNumber),
        previousPFNumber: encrypt(personalDetails.previousPFNumber),
        accountNumber: encrypt(personalDetails.accountNumber),
        iFSCCode: encrypt(personalDetails.iFSCCode),
        passportNo: encrypt(personalDetails.passportNo),
        schemeCertificateNumber: encrypt(
          personalDetails.schemeCertificateNumber
        ),
        familyMembers: updatedFamilyMembers,
        nationality: personalDetails.nationality ?? 0,
        declarationDate: moment(new Date()).format("YYYY-MM-DDT00:00:00")
      }
    };
    toast.configure();

    try {
      const res = await api.saveOnBoardingDetails(onBoardingDetails);

      if (res && !res.errorMessage) {
        !disableToast &&
          toast.success("Saved Successfully ", defaultToastOptions);
        getPersonalDetails(generalDetails.id);
        if (isLoggedInUserAdminOrHROPs && isSubmit) {
          history.push("/CandidateList");
        } else if (isSubmit) {
          setHasUserSubmitted(true);
        }
      } else {
        !disableToast &&
          toast.error(`Error while Saving Details`, defaultToastOptions);
      }
    } catch (e) {
      !disableToast &&
        toast.error(`Error while Saving Details`, defaultToastOptions);
    }
  };
  const findCurrentStep = step => {
    switch (step) {
      case "PersonalDetailsForm":
        return (
          <Form ref={PersonalDetailRef}>
            <PersonalDetailsForm
              personalDetails={personalDetails}
              genders={genders}
              loggedInUserId={user.roleId}
              maritalStatusList={maritalStatusList}
              addressDetails={addressDetails}
              states={states}
              updatePersonalDetails={updatePersonalDetails}
            />
          </Form>
        );
      case "ContactDetailsForm":
        return (
          <Form ref={ContactDetailRef}>
            <ContactDetailsForm
              personalDetails={personalDetails}
              states={states}
              generalDetails={generalDetails}
              relationShipList={relationShipList}
              updatePersonalDetails={updatePersonalDetails}
              getRelationShipList={getRelationShipList}
              addRelationDetails={addRelationDetails}
              deleteRelationDetails={deleteRelationDetails}
            />
          </Form>
        );
      case "GratuityFormF":
        return (
          <Form ref={GratuityFormRef}>
            <GratuityFormF
              relationShipList={relationShipList}
              genders={genders}
              maritalStatusList={maritalStatusList}
              personalDetails={personalDetails}
              states={states}
              updatePersonalDetails={updatePersonalDetails}
              addGratuityDetails={addGratuityDetails}
              deleteGratuityDetails={deleteGratuityDetails}
              updateNomineeDetails={updateNomineeDetails}
            />
          </Form>
        );
      case "EPFForm2":
        return (
          <Form ref={EPFForm2Ref}>
            <EPFForm2
              relationShipList={relationShipList}
              genders={genders}
              maritalStatusList={maritalStatusList}
              personalDetails={personalDetails}
              states={states}
              updatePersonalDetails={updatePersonalDetails}
              addEPFFrom2Details={addEPFFrom2Details}
              deleteEPFFrom2Details={deleteEPFFrom2Details}
              updateNomineeDetails={updateNomineeDetails}
            />
          </Form>
        );
      case "EPFPartB":
        return (
          <EPFPartB
            personalDetails={personalDetails}
            getRelationShipList={getRelationShipList}
            relationShipList={relationShipList}
            updatePersonalDetails={updatePersonalDetails}
          />
        );
      case "EPFForm11":
        return (
          <Form ref={EPFForm11Ref}>
            <EPFForm11
              personalDetails={personalDetails}
              genders={genders}
              maritalStatusList={maritalStatusList}
              updatePersonalDetails={updatePersonalDetails}
            />
          </Form>
        );
      case "IdentityCardForm":
        return (
          <Form ref={SEZFormRef}>
            <IdentityCardForm
              personalDetails={personalDetails}
              updatePersonalDetails={updatePersonalDetails}
              states={states}
            />
          </Form>
        );
      case "CSRForm":
        return (
          <Form ref={CSRFormRef}>
            <CSRForm
              personalDetails={personalDetails}
              updatePersonalDetails={updatePersonalDetails}
            />
          </Form>
        );
      case "BankingInformation":
        return (
          <Form ref={BankFormRef}>
            <BankingInformation
              otherBankDetails={otherBankDetails}
              personalDetails={personalDetails}
              updateOtherBankDetails={updateOtherBankDetails}
              updatePersonalDetails={updatePersonalDetails}
            />
          </Form>
        );
      case "ConfidentialityAgreement":
        return (
          <ConfidentialityAgreement
            personalDetails={personalDetails}
            updatePersonalDetails={updatePersonalDetails}
          />
        );
      default:
        return (
          <PersonalDetailsForm
            personalDetails={personalDetails}
            genders={genders}
            maritalStatusList={maritalStatusList}
            addressDetails={addressDetails}
            updatePersonalDetails={updatePersonalDetails}
          />
        );
    }
  };

  const isEPFNomineeAdded = () => {
    return (
      personalDetails.ePFNomineeDetails &&
      personalDetails.ePFNomineeDetails.length
    );
  };

  const isGratuityNomineeAdded = () => {
    return (
      personalDetails.gratuityNomination &&
      personalDetails.gratuityNomination.length
    );
  };

  const isWidowPensionNomineeSelected = () => {
    return personalDetails.familyMembers.some(
      familyMember => familyMember.isEPSNominee
    );
  };

  if (!allowFormSubmission) {
    return <Fragment />;
  }

  const stepsWithDesc = steps.map(step => onBoardingSteps[step]);
  return (
    <Fragment>
      <h6 className="ml-3 pt-2">
        {user && user.roleId === 3 ? (
          <p>
            Please fill all the mandatory fields, click on <b>SAVE INFO</b> and
            then click on <b>NEXT</b>.
          </p>
        ) : (
          <Link className="text-decoration-none back-link" to="/CandidateList">
            Back to Candidate List
          </Link>
        )}
      </h6>
      <HorizontalLinearStepper
        className="mb-2"
        steps={stepsWithDesc}
        activeStep={activeStep}
        finished={false}
      />
      <Container>
        <div className="pb-5 mb-3">{findCurrentStep(steps[activeStep])}</div>
      </Container>

      <Navbar fixed="bottom" className="footer">
        <Row className="w-100">
          <Col md="9">
            {activeStep !== 0 && (
              <Button
                onClick={() => {
                  scrollToTopLeft();
                  setActiveStep(prevActiveStep => prevActiveStep - 1);
                }}
                variant="outline-light"
              >
                 BACK TO {stepsWithDesc[activeStep - 1].toUpperCase()}
              </Button>
            )}
          </Col>

          <Col md="2" className="text-right">
            <Button
              className="btn-controls"
              onClick={saveOnBoardingDetails}
              disabled={!isValid}
            >
              SAVE INFO
            </Button>
          </Col>

          <Col md="1" className="text-right">
          {activeStep !== steps.length - 1 ? (
              <Button
                className="btn-controls"
                onClick={evt => {
                  scrollToTopLeft();
                  steps[activeStep] === "ContactDetailsForm" &&
                    saveOnBoardingDetails(evt, false, true);
                  setActiveStep(prevActiveStep => prevActiveStep + 1);
                }}
                disabled={!isValid}
              >
                NEXT
              </Button>
            ) : (
              <Button
                className="btn-controls"
                onClick={evt => saveOnBoardingDetails(evt, true)}
                disabled={!isValid || !personalDetails.isConfidentialAgreement}
              >
                SUBMIT
              </Button>
            )}
          </Col>
        </Row>
      </Navbar>
    </Fragment>
  );
};

const mapStateToProps = ({ onBoardingDetails, candidateDetails }) => ({
  addressDetails: onBoardingDetails.personalDetails.addresses || [],
  genders: candidateDetails.genders || [],
  generalDetails: onBoardingDetails.generalDetails || {},
  maritalStatusList: candidateDetails.maritalStatusList || [],
  otherBankDetails: onBoardingDetails.otherBankDetails || {},
  personalDetails: onBoardingDetails.personalDetails || {},
  relationShipList: onBoardingDetails.relationShipList || [],
  states: candidateDetails.states || []
});

const mapDispatchToProps = {
  addEPFFrom2Details: onBoardingActions.addEPFFrom2Details,
  addGratuityDetails: onBoardingActions.addGratuityDetails,
  addRelationDetails: onBoardingActions.addRelationDetails,
  deleteEPFFrom2Details: onBoardingActions.deleteEPFFrom2Details,
  deleteGratuityDetails: onBoardingActions.deleteGratuityDetails,
  deleteRelationDetails: onBoardingActions.deleteRelationDetails,
  getGendersList: candidateActions.getGendersList,
  getMaritalStatusList: candidateActions.getMaritalStatusList,
  getPersonalDetails: onBoardingActions.getPersonalDetails,
  getRelationShipList: onBoardingActions.getRelationShipList,
  getStatesList: candidateActions.getStatesList,
  updateNomineeDetails: onBoardingActions.updateNomineeDetails,
  updateOtherBankDetails: onBoardingActions.updateOtherBankDetails,
  updatePersonalDetails: onBoardingActions.updatePersonalDetails
};

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingForms);
